import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import {
  Briefcase, MapPin, Building2, Search, ArrowRight, Clock, Filter, X,
  FileText, Upload, ChevronDown, ChevronUp
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { ThemeToggle } from '../components/ThemeToggle';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_agency-network-hub/artifacts/d66f2cg2_IMG_0019.png";

const JOB_TYPES = [
  { value: 'full_time', label: 'Full Time' },
  { value: 'part_time', label: 'Part Time' },
  { value: 'contract', label: 'Contract' },
  { value: 'permanent', label: 'Permanent' },
  { value: 'trainee', label: 'Trainee / Supernumerary' },
];

export function JobListings() {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [search, setSearch] = useState('');
  const [locationSearch, setLocationSearch] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(true);
  const [applyModal, setApplyModal] = useState(null);
  const [applyForm, setApplyForm] = useState({
    cover_letter: '', experience: '', availability: '',
    cv_data: '', cv_filename: '', documents: []
  });
  const [submitting, setSubmitting] = useState(false);
  const [expandedJob, setExpandedJob] = useState(null);

  useEffect(() => {
    fetchJobs();
  }, [selectedType, locationSearch]);

  const fetchJobs = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedType) params.append('job_type', selectedType);
      if (locationSearch) params.append('location', locationSearch);
      const { data } = await axios.get(`${API_URL}/api/public/jobs?${params.toString()}`);
      setJobs(data);
    } catch (err) {
      console.error('Failed to fetch jobs:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredJobs = jobs.filter(job =>
    job.title?.toLowerCase().includes(search.toLowerCase()) ||
    job.organization_name?.toLowerCase().includes(search.toLowerCase()) ||
    job.description?.toLowerCase().includes(search.toLowerCase())
  );

  // Group by job type
  const groupedJobs = {};
  JOB_TYPES.forEach(t => { groupedJobs[t.value] = []; });
  groupedJobs['other'] = [];
  filteredJobs.forEach(job => {
    const type = job.job_type || 'other';
    if (groupedJobs[type]) {
      groupedJobs[type].push(job);
    } else {
      groupedJobs['other'].push(job);
    }
  });

  const clearFilters = () => {
    setSelectedType('');
    setLocationSearch('');
    setSearch('');
  };

  const hasActiveFilters = selectedType || locationSearch || search;
  const showGrouped = !selectedType && !search && !locationSearch;

  const handleApplyClick = (job) => {
    if (!isAuthenticated) {
      toast.info('Please sign up or sign in to apply for jobs');
      navigate('/login', { state: { from: { pathname: '/jobs' } } });
      return;
    }
    setApplyModal(job);
  };

  const handleFileUpload = (e, field) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File must be under 5MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      if (field === 'cv') {
        setApplyForm(prev => ({ ...prev, cv_data: reader.result, cv_filename: file.name }));
      } else {
        setApplyForm(prev => ({
          ...prev,
          documents: [...prev.documents, { filename: file.name, data: reader.result }]
        }));
      }
    };
    reader.readAsDataURL(file);
  };

  const removeDocument = (index) => {
    setApplyForm(prev => ({
      ...prev,
      documents: prev.documents.filter((_, i) => i !== index)
    }));
  };

  const handleSubmitApplication = async (e) => {
    e.preventDefault();
    if (!applyForm.cover_letter.trim()) {
      toast.error('Please write a cover letter');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/public/jobs/${applyModal.id}/apply`, applyForm, { withCredentials: true });
      toast.success('Application submitted! The agency will review it shortly.');
      setApplyModal(null);
      setApplyForm({ cover_letter: '', experience: '', availability: '', cv_data: '', cv_filename: '', documents: [] });
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to submit application';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const typeColors = {
    full_time: '#00A6A0',
    part_time: '#4A90E2',
    contract: '#FF6F61',
    permanent: '#A8E6CF',
    trainee: '#F97316',
    other: '#94A3B8'
  };

  const renderJobCard = (job) => {
    const isExpanded = expandedJob === job.id;
    return (
      <Card key={job.id} className="glass-card glass-card-hover" data-testid={`job-card-${job.id}`}>
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
            <div className="flex items-start gap-4 flex-1 min-w-0">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${typeColors[job.job_type] || '#94A3B8'}15` }}>
                <Briefcase className="w-6 h-6" style={{ color: typeColors[job.job_type] || '#94A3B8' }} />
              </div>
              <div className="min-w-0">
                <h3 className="text-lg font-heading font-semibold text-foreground">
                  {job.title}
                </h3>
                <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Building2 className="w-3.5 h-3.5" />
                    {job.organization_name || 'Agency'}
                  </span>
                  {job.location && (
                    <span className="flex items-center gap-1">
                      <MapPin className="w-3.5 h-3.5" />
                      {job.location}
                    </span>
                  )}
                  {job.job_type_label && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs"
                      style={{
                        backgroundColor: `${typeColors[job.job_type] || '#94A3B8'}15`,
                        color: typeColors[job.job_type] || '#94A3B8'
                      }}>
                      <Clock className="w-3 h-3" />
                      {job.job_type_label}
                    </span>
                  )}
                </div>
                {job.salary_range && (
                  <p className="text-sm text-[#00A6A0] font-medium mt-2">{job.salary_range}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setExpandedJob(isExpanded ? null : job.id)}
                className="text-muted-foreground"
                data-testid={`expand-job-${job.id}`}
              >
                {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </Button>
              <Button
                onClick={() => handleApplyClick(job)}
                className="btn-primary"
                data-testid={`apply-job-${job.id}`}
              >
                Apply Now
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>

          {isExpanded && (
            <div className="mt-4 pt-4 border-t border-border space-y-3 animate-in slide-in-from-top-1">
              {job.description && (
                <div>
                  <h4 className="text-sm font-semibold text-foreground mb-1">Description</h4>
                  <p className="text-sm text-muted-foreground whitespace-pre-line">{job.description}</p>
                </div>
              )}
              {job.requirements?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-foreground mb-1">Requirements</h4>
                  <ul className="list-disc list-inside text-sm text-muted-foreground space-y-0.5">
                    {job.requirements.map((r, i) => <li key={i}>{r}</li>)}
                  </ul>
                </div>
              )}
              {job.benefits?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-foreground mb-1">Benefits</h4>
                  <ul className="list-disc list-inside text-sm text-muted-foreground space-y-0.5">
                    {job.benefits.map((b, i) => <li key={i}>{b}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-background relative">
      <div className="glass-blobs"><div className="blob-3" /></div>
      <div className="noise-overlay" />

      {/* Header */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass-nav">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img src={LOGO_URL} alt="FendanaCura" className="h-8 sm:h-9 w-auto" />
              <span className="text-lg sm:text-xl font-heading font-bold text-foreground">FendanaCura</span>
            </Link>
            <div className="hidden md:flex items-center gap-6">
              <Link to="/browse" className="text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-browse">
                Browse Agencies
              </Link>
              <Link to="/register-company" className="text-muted-foreground hover:text-foreground transition-colors">
                Register Agency
              </Link>
            </div>
            <div className="flex items-center gap-2">
              <ThemeToggle />
              {isAuthenticated ? (
                <Link to="/dashboard">
                  <Button className="btn-primary" data-testid="go-dashboard-btn">Dashboard</Button>
                </Link>
              ) : (
                <Link to="/login">
                  <Button variant="outline" className="border-[#00A6A0] text-[#00A6A0] hover:bg-[#00A6A0]/10 font-medium" data-testid="sign-in-btn">Sign In</Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-12 relative z-10">
        <div className="text-center mb-10">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-foreground mb-4">
            Caregiver Job Opportunities
          </h1>
          <p className="text-muted-foreground max-w-2xl mx-auto text-base sm:text-lg">
            Find rewarding positions at care agencies. Apply after signing up to manage your applications.
          </p>
        </div>

        {/* Search & Filter Bar */}
        <div className="max-w-3xl mx-auto mb-8 space-y-4">
          <div className="flex gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by title or agency..."
                className="w-full pl-12 py-6 bg-card border-border"
                data-testid="job-search-input"
              />
            </div>
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className={`btn-secondary py-6 px-4 ${hasActiveFilters ? 'border-[#00A6A0] text-[#00A6A0]' : ''}`}
              data-testid="toggle-filters-btn"
            >
              <Filter className="w-5 h-5" />
              {hasActiveFilters && <span className="ml-1 w-2 h-2 rounded-full bg-[#00A6A0]" />}
            </Button>
          </div>

          {/* Filter Panel */}
          {showFilters && (
            <div className="glass-card rounded-xl p-6 space-y-5 animate-in slide-in-from-top-2" data-testid="filter-panel">
              <div className="flex items-center justify-between">
                <h3 className="font-heading font-semibold text-foreground">Filters</h3>
                {hasActiveFilters && (
                  <button onClick={clearFilters} className="text-sm text-[#00A6A0] hover:underline flex items-center gap-1" data-testid="clear-filters-btn">
                    <X className="w-3 h-3" /> Clear all
                  </button>
                )}
              </div>

              <div>
                <Label className="text-sm text-muted-foreground mb-2 block">Job Type</Label>
                <div className="flex flex-wrap gap-2">
                  {JOB_TYPES.map(type => (
                    <button
                      key={type.value}
                      onClick={() => setSelectedType(selectedType === type.value ? '' : type.value)}
                      className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                        selectedType === type.value
                          ? 'bg-[#00A6A0] text-white'
                          : 'bg-muted text-muted-foreground hover:bg-muted/80'
                      }`}
                      data-testid={`filter-type-${type.value}`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-sm text-muted-foreground mb-2 block">Location</Label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    value={locationSearch}
                    onChange={(e) => setLocationSearch(e.target.value)}
                    placeholder="City, region, or postcode..."
                    className="pl-10 bg-card border-border"
                    data-testid="location-search-input"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Results */}
        {isLoading ? (
          <div className="text-center py-16">
            <div className="spinner mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading jobs...</p>
          </div>
        ) : filteredJobs.length === 0 ? (
          <div className="text-center py-16">
            <Briefcase className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-xl font-heading font-semibold text-foreground mb-2">
              No Jobs Available
            </h3>
            <p className="text-muted-foreground mb-4">
              {hasActiveFilters ? 'Try adjusting your filters' : 'Check back later for new opportunities'}
            </p>
            <div className="flex gap-3 justify-center">
              {hasActiveFilters && (
                <Button variant="outline" onClick={clearFilters} className="btn-secondary" data-testid="clear-search-btn">
                  Clear Filters
                </Button>
              )}
              <Link to="/browse">
                <Button variant="outline" className="btn-secondary">
                  Browse Agencies Instead
                </Button>
              </Link>
            </div>
          </div>
        ) : showGrouped ? (
          /* Grouped by type */
          <div className="space-y-10">
            {JOB_TYPES.map(type => {
              const group = groupedJobs[type.value];
              if (!group?.length) return null;
              return (
                <div key={type.value} data-testid={`job-group-${type.value}`}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: typeColors[type.value] }} />
                    <h2 className="text-lg font-heading font-semibold text-foreground">{type.label}</h2>
                    <span className="text-sm text-muted-foreground">({group.length})</span>
                  </div>
                  <div className="space-y-4">
                    {group.map(renderJobCard)}
                  </div>
                </div>
              );
            })}
            {groupedJobs['other']?.length > 0 && (
              <div data-testid="job-group-other">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-3 h-3 rounded-full bg-muted-foreground" />
                  <h2 className="text-lg font-heading font-semibold text-foreground">Other</h2>
                  <span className="text-sm text-muted-foreground">({groupedJobs['other'].length})</span>
                </div>
                <div className="space-y-4">
                  {groupedJobs['other'].map(renderJobCard)}
                </div>
              </div>
            )}
          </div>
        ) : (
          /* Flat list */
          <div className="space-y-4">
            {filteredJobs.map(renderJobCard)}
          </div>
        )}

        {/* Sign-up prompt for unauthenticated users */}
        {!isAuthenticated && filteredJobs.length > 0 && (
          <div className="mt-12 text-center glass-card rounded-2xl p-8">
            <h3 className="text-xl font-heading font-bold text-foreground mb-2">
              Ready to apply?
            </h3>
            <p className="text-muted-foreground mb-6">
              Sign up to submit applications, upload your CV, and track your progress.
            </p>
            <div className="flex gap-3 justify-center">
              <Link to="/login">
                <Button className="btn-primary px-8">
                  Sign Up / Sign In
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>

      {/* Application Modal */}
      {applyModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-testid="apply-modal">
          <div className="glass-card rounded-2xl p-8 max-w-lg w-full relative max-h-[90vh] overflow-y-auto">
            <button onClick={() => setApplyModal(null)} className="absolute top-4 right-4 text-muted-foreground hover:text-foreground">
              <X className="w-5 h-5" />
            </button>
            <h2 className="text-xl font-heading font-bold text-foreground mb-1">Apply for Position</h2>
            <p className="text-sm text-muted-foreground mb-6">
              <span className="text-[#00A6A0] font-medium">{applyModal.title}</span> at {applyModal.organization_name}
            </p>
            <form onSubmit={handleSubmitApplication} className="space-y-4">
              <div>
                <Label className="text-foreground">Cover Letter *</Label>
                <textarea
                  value={applyForm.cover_letter}
                  onChange={(e) => setApplyForm({ ...applyForm, cover_letter: e.target.value })}
                  placeholder="Tell the agency why you're a great fit..."
                  rows={5}
                  className="w-full mt-1 rounded-md border border-border bg-card text-foreground px-3 py-2 text-sm resize-none"
                  required
                  data-testid="apply-cover-letter"
                />
              </div>

              <div>
                <Label className="text-foreground">Upload CV / Resume</Label>
                <div className="mt-1">
                  {applyForm.cv_filename ? (
                    <div className="flex items-center gap-2 p-3 rounded-lg bg-muted">
                      <FileText className="w-4 h-4 text-[#00A6A0]" />
                      <span className="text-sm text-foreground flex-1 truncate">{applyForm.cv_filename}</span>
                      <button
                        type="button"
                        onClick={() => setApplyForm(prev => ({ ...prev, cv_data: '', cv_filename: '' }))}
                        className="text-muted-foreground hover:text-destructive"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <label className="flex items-center justify-center gap-2 p-4 rounded-lg border-2 border-dashed border-border cursor-pointer hover:border-[#00A6A0] transition-colors" data-testid="cv-upload-area">
                      <Upload className="w-5 h-5 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">Click to upload CV (PDF, DOC — max 5MB)</span>
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx"
                        className="hidden"
                        onChange={(e) => handleFileUpload(e, 'cv')}
                        data-testid="cv-file-input"
                      />
                    </label>
                  )}
                </div>
              </div>

              <div>
                <Label className="text-foreground">Additional Documents</Label>
                <div className="mt-1 space-y-2">
                  {applyForm.documents.map((doc, i) => (
                    <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-muted">
                      <FileText className="w-4 h-4 text-[#4A90E2]" />
                      <span className="text-sm text-foreground flex-1 truncate">{doc.filename}</span>
                      <button type="button" onClick={() => removeDocument(i)} className="text-muted-foreground hover:text-destructive">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <label className="flex items-center justify-center gap-2 p-3 rounded-lg border border-dashed border-border cursor-pointer hover:border-[#4A90E2] transition-colors" data-testid="doc-upload-area">
                    <Upload className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Add document (certifications, references...)</span>
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx,.jpg,.png"
                      className="hidden"
                      onChange={(e) => handleFileUpload(e, 'doc')}
                      data-testid="doc-file-input"
                    />
                  </label>
                </div>
              </div>

              <div>
                <Label className="text-foreground">Experience</Label>
                <textarea
                  value={applyForm.experience}
                  onChange={(e) => setApplyForm({ ...applyForm, experience: e.target.value })}
                  placeholder="Briefly describe your care experience..."
                  rows={3}
                  className="w-full mt-1 rounded-md border border-border bg-card text-foreground px-3 py-2 text-sm resize-none"
                  data-testid="apply-experience"
                />
              </div>

              <div>
                <Label className="text-foreground">Availability</Label>
                <Input
                  value={applyForm.availability}
                  onChange={(e) => setApplyForm({ ...applyForm, availability: e.target.value })}
                  placeholder="e.g. Weekdays 9-5, flexible weekends"
                  className="bg-card border-border mt-1"
                  data-testid="apply-availability"
                />
              </div>

              <Button type="submit" disabled={submitting} className="w-full btn-primary py-5" data-testid="apply-submit-btn">
                {submitting ? 'Submitting...' : 'Submit Application'}
              </Button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default JobListings;
